import type { Pool } from "./data";
import { POOL_DISPLAY_ORDER_PRIORITY, type PoolTokenCategory } from "./types";

const TOKEN_SYMBOL_ALIASES: Record<string, string> = {
	ETH: "WETH",
	BTC: "WBTC",
};

const ETHEREUM_TOKENS = new Set(["ETH", "WETH"]);
const BITCOIN_TOKENS = new Set(["BTC", "WBTC", "CBBTC"]);
/** Keep in sync with backend pool writers; bridged symbols use normalizeStableSymbolKey. */
const STABLECOINS = new Set([
	"USDC",
	"USDT",
	"DAI",
	"PYUSD",
	"USDS",
	"USD₮0",
	"USDC.E",
	"FRAX",
	"GHO",
	"LUSD",
	"SUSD",
	"TUSD",
	"BUSD",
	"USDD",
	"CRVUSD",
	"USDE",
	"FDUSD",
	"EURC",
	"USDBC",
	// Robinhood Chain canonical USD stable
	"USDG",
]);

const VALID_POOL_TOKEN_CATEGORIES = new Set<PoolTokenCategory>([
	"ETH",
	"BTC",
	"Stablecoin",
	"Altcoin",
]);
const DISPLAY_ORDER_RANK = new Map(
	POOL_DISPLAY_ORDER_PRIORITY.map((category, index) => [category, index]),
);

interface DisplayToken {
	symbol: string;
	address?: string;
	category?: PoolTokenCategory;
}

function normalizeStableSymbolKey(symbol: string): string {
	return symbol.trim().toUpperCase().replace(/\./g, "");
}

export function isStablecoinSymbol(symbol: string): boolean {
	const upper = symbol.trim().toUpperCase();
	if (STABLECOINS.has(upper)) return true;
	const compact = normalizeStableSymbolKey(symbol);
	if (STABLECOINS.has(compact)) return true;
	// Bridged USDC (USDC.e, USDC.E, etc.) — avoid matching unrelated USDC* tickers.
	if (compact.startsWith("USDC") && compact.length > 4 && compact.length <= 8) return true;
	return false;
}

function resolveTokenCategoryFromPool(
	symbol: string,
	category: PoolTokenCategory | undefined,
): PoolTokenCategory {
	if (category && VALID_POOL_TOKEN_CATEGORIES.has(category)) return category;
	return getTokenCategory(symbol);
}

function resolvePoolTokenCategories(pool: Pool): {
	token1Category: PoolTokenCategory;
	token2Category: PoolTokenCategory;
} {
	return {
		token1Category: resolveTokenCategoryFromPool(pool.token1, pool.token1Category),
		token2Category: resolveTokenCategoryFromPool(pool.token2, pool.token2Category),
	};
}

function normalizeSymbol(symbol: string): string {
	const upper = symbol.trim().toUpperCase();
	return TOKEN_SYMBOL_ALIASES[upper] ?? upper;
}

function compareAddresses(a?: string, b?: string): number {
	if (!a || !b) return 0;
	return a.toLowerCase().localeCompare(b.toLowerCase());
}

function getCategoryRank(category: PoolTokenCategory): number {
	return DISPLAY_ORDER_RANK.get(category) ?? DISPLAY_ORDER_RANK.size;
}

function compareDisplayTokens(left: DisplayToken, right: DisplayToken): number {
	const leftCategory = left.category ?? getTokenCategory(left.symbol);
	const rightCategory = right.category ?? getTokenCategory(right.symbol);

	// Altcoin/Stablecoin filter card is "Altcoin / Stablecoin" — volatile first, stable second.
	if (leftCategory === "Altcoin" && rightCategory === "Stablecoin") return -1;
	if (leftCategory === "Stablecoin" && rightCategory === "Altcoin") return 1;

	const categoryDiff = getCategoryRank(leftCategory) - getCategoryRank(rightCategory);
	if (categoryDiff !== 0) return categoryDiff;

	const leftNormalized = normalizeSymbol(left.symbol);
	const rightNormalized = normalizeSymbol(right.symbol);
	const normalizedDiff = leftNormalized.localeCompare(rightNormalized);
	if (normalizedDiff !== 0) return normalizedDiff;

	const rawDiff = left.symbol.trim().toUpperCase().localeCompare(right.symbol.trim().toUpperCase());
	if (rawDiff !== 0) return rawDiff;

	return compareAddresses(left.address, right.address);
}

export function getTokenCategory(token: string): PoolTokenCategory {
	if (isStablecoinSymbol(token)) return "Stablecoin";
	const upper = token.trim().toUpperCase();
	if (BITCOIN_TOKENS.has(upper)) return "BTC";
	if (ETHEREUM_TOKENS.has(upper)) return "ETH";
	return "Altcoin";
}

export type DisplayTokenOrderMatch = "same" | "swapped" | "unmatched";

function tokenMatchKey(symbol: string, collapseStables: boolean): string {
	const cat = getTokenCategory(symbol);
	if (cat === "ETH" || cat === "BTC") return cat;
	if (collapseStables && cat === "Stablecoin") return "STABLE";
	return normalizeSymbol(symbol);
}

/**
 * Align catalog display symbols (ETH/USDC) with GeckoTerminal base/quote (USDG/WETH).
 * Collapses ETH/WETH, BTC/WBTC, and — for volatile/stable pairs only — distinct USD stables.
 */
export function matchDisplayTokenOrder(
	display1: string | null | undefined,
	display2: string | null | undefined,
	raw1: string,
	raw2: string,
): DisplayTokenOrderMatch {
	if (!display1?.trim() || !display2?.trim()) return "unmatched";

	const d1Cat = getTokenCategory(display1);
	const d2Cat = getTokenCategory(display2);
	const r1Cat = getTokenCategory(raw1);
	const r2Cat = getTokenCategory(raw2);
	const collapseStables =
		(d1Cat === "Stablecoin") !== (d2Cat === "Stablecoin") &&
		(r1Cat === "Stablecoin") !== (r2Cat === "Stablecoin");

	const d1 = tokenMatchKey(display1, collapseStables);
	const d2 = tokenMatchKey(display2, collapseStables);
	const r1 = tokenMatchKey(raw1, collapseStables);
	const r2 = tokenMatchKey(raw2, collapseStables);

	if (d1 === r1 && d2 === r2) return "same";
	if (d1 === r2 && d2 === r1) return "swapped";
	return "unmatched";
}

/** Default LP min/max half-width as a fraction of current price (Analyze tab). */
export function getDefaultAnalyzeRangePct(
	pool: Pick<Pool, "token1Category" | "token2Category">,
): number {
	const c1 = pool.token1Category;
	const c2 = pool.token2Category;
	if (c1 === "Stablecoin" && c2 === "Stablecoin") return 0.0005;
	const cats = new Set([c1, c2]);
	if (cats.has("ETH") && cats.has("Stablecoin")) return 0.05;
	if (cats.has("BTC") && cats.has("Stablecoin")) return 0.04;
	if (cats.has("ETH") && cats.has("BTC")) return 0.075;
	if (cats.has("Altcoin") && cats.has("Stablecoin")) return 0.06;
	return 0.1;
}

/**
 * When exactly one side is a stablecoin, default the toggle so the **stable** is primary
 * (left/active in the UI). Price is then ~3000 USDC/ETH instead of tiny ETH/USDC decimals.
 */
export function getStablePrimarySelectedCoin(
	pool: Pick<Pool, "token1Category" | "token2Category">,
): "token1" | "token2" | null {
	const s1 = pool.token1Category === "Stablecoin";
	const s2 = pool.token2Category === "Stablecoin";
	if (s1 && !s2) return "token1";
	if (!s1 && s2) return "token2";
	return null;
}

export function normalizePoolDisplayPair(pool: Pool): Pool {
	const token1 = pool.token1.trim();
	const token2 = pool.token2.trim();
	const categories = resolvePoolTokenCategories(pool);
	const shouldSwap =
		compareDisplayTokens(
			{
				symbol: token1,
				address: pool.token0Address,
				category: categories.token1Category,
			},
			{
				symbol: token2,
				address: pool.token1Address,
				category: categories.token2Category,
			},
		) > 0;

	const normalized: Pool = shouldSwap
		? {
				...pool,
				token1: token2,
				token2: token1,
				token1Category: categories.token2Category,
				token2Category: categories.token1Category,
				// Display-scoped CoinGecko fields track token1/token2 (not canonical
				// token0Address/token1Address), so they must flip whenever the display
				// pair does — otherwise token names link to the wrong CoinGecko coin.
				coinGeckoDisplayToken1Id: pool.coinGeckoDisplayToken2Id,
				coinGeckoDisplayToken2Id: pool.coinGeckoDisplayToken1Id,
				displayToken1MarketCapRank: pool.displayToken2MarketCapRank,
				displayToken2MarketCapRank: pool.displayToken1MarketCapRank,
				displayToken1MarketCapUsd: pool.displayToken2MarketCapUsd,
				displayToken2MarketCapUsd: pool.displayToken1MarketCapUsd,
			}
		: {
				...pool,
				token1,
				token2,
				token1Category: categories.token1Category,
				token2Category: categories.token2Category,
			};

	assertCanonicalPoolOrderingUnchanged(pool, normalized);
	return normalized;
}

export function assertCanonicalPoolOrderingUnchanged(original: Pool, normalized: Pool): void {
	if (
		original.token0Address !== normalized.token0Address ||
		original.token1Address !== normalized.token1Address
	) {
		throw new Error(
			`Display normalization mutated canonical token ordering for pool ${original.pool}`,
		);
	}
}
